import { NOTIFICATION_PHRASES } from "@onadssuper/validators";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Inbox as InboxIcon } from "lucide-react";
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  PageHeader,
  PageSpinner,
} from "../../../components/ui";
import { useCurrentWorkspace } from "../../../features/workspace/workspace-context";
import { Avatar } from "../../../lib/avatar";
import { useTRPC } from "../../../lib/trpc";
import { formatDateTime } from "../../../lib/utils";

export const Route = createFileRoute("/w/$slug/inbox")({ component: Inbox });

function Inbox() {
  const trpc = useTRPC();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const inbox = useInfiniteQuery(
    trpc.notifications.list.infiniteQueryOptions(
      { limit: 20 },
      { getNextPageParam: (last) => last.nextCursor ?? undefined },
    ),
  );
  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.notifications.pathKey() });
  const markRead = useMutation(trpc.notifications.markRead.mutationOptions({ onSuccess: refresh }));
  const markAllRead = useMutation(
    trpc.notifications.markAllRead.mutationOptions({ onSuccess: refresh }),
  );
  const items = inbox.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="Inbox"
        description="What's waiting on you and what's happened to your work."
        actions={
          items.some((n) => !n.readAt) && (
            <Button
              variant="secondary"
              loading={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </Button>
          )
        }
      />
      {inbox.isPending ? (
        <PageSpinner />
      ) : inbox.isError ? (
        <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState icon={<InboxIcon className="size-8" />} title="Nothing here yet" />
      ) : (
        <Card>
          <CardBody>
            <ul className="flex flex-col gap-4">
              {items.map((item) => (
                <li key={item.id} className="flex items-start gap-3 text-sm">
                  <Avatar name={item.actor?.name ?? "System"} image={item.actor?.image} />
                  <div className="min-w-0 flex-1">
                    <p className={item.readAt ? "text-muted" : "font-medium"}>
                      {item.actor?.name ?? "Someone"} {NOTIFICATION_PHRASES[item.kind]} on{" "}
                      <Link
                        to="/w/$slug/campaigns/$campaignId"
                        params={{ slug: workspace.slug, campaignId: item.campaignId }}
                        className="underline"
                      >
                        {item.campaign.name}
                      </Link>
                    </p>
                    <p className="text-xs text-subtle">{formatDateTime(item.createdAt)}</p>
                  </div>
                  {!item.readAt && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={markRead.isPending}
                      onClick={() => markRead.mutate({ notificationIds: [item.id] })}
                    >
                      Mark read
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      {inbox.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="secondary"
            loading={inbox.isFetchingNextPage}
            onClick={() => void inbox.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
    </>
  );
}
